/**
 * **The Engineer's prototype weapon settings** (`t_engineer`, Core p.98; `ll1-features` 8.6),
 * frozen onto the talent-integrated Prototype Weapon at spawn from the mech's roster record
 * (`MechData.prototype`, set by `set_mech_prototype`):
 * - `weaponType` (every rank): the weapon's type, and its reach with it: Threat 1 for Melee, Range
 *   10 for every other type (the printed profile carries both). Absent, the weapon stays as printed.
 * - `damageType` (every rank): the damage type in place of the printed Variable, so no per-attack
 *   pick is asked. Absent, the attack asks (`resolve/attackPreRoll.ts`).
 * - `limitedUses`: the player's Limited roll (1d6+2, or 2d6 at rank 3), in place of the frozen
 *   minimum `tagVal` reads off `"1d6+2"`/`"2d6"`.
 * - Revisions (rank 2 and up, Prototype Weapon II and III):
 *   - **Tweaked Optics:** +1 Accuracy on attacks with the weapon (`WeaponProfile.accuracy`, read by the to-hit).
 *   - **Tweaked Computer:** the weapon is Smart (`WeaponProfile.smart`).
 *   - **Stripped Reactor Shielding:** four alternate firing profiles, each costing 2 heat on top
 *     of the weapon's own (Heat (Self)): Cone 3, Line 5, Blast 1 within Range 10, Burst 1. The
 *     attack picks one by `RequestAttack.profile` (the CLI's `--profile`). RAW offers the first
 *     three to a ranged type and Burst 1 to a melee type; with no `weaponType` recorded, all four
 *     are offered.
 * Absent settings leave the weapon as frozen.
 */
import type { PrototypeConfig, WeaponProfile, WeaponRange } from "@lancer-vtt/domain";

/** The Engineer's three prototype weapons, by rank. */
const PROTOTYPE_WEAPON_IDS = new Set(["mw_prototype_1", "mw_prototype_2", "mw_prototype_3"]);
/** The ranks that take Revisions (rank 2, Revision; rank 3, Final Draft). */
const REVISED_WEAPON_IDS = new Set(["mw_prototype_2", "mw_prototype_3"]);

/** Stripped Reactor Shielding's attack options: a profile id suffix, its label, its range. */
const SHIELDING: { key: string; label: string; range: WeaponRange[]; melee: boolean }[] = [
  { key: "shielding_cone3", label: "Cone 3", range: [{ kind: "cone", val: 3 }], melee: false },
  { key: "shielding_line5", label: "Line 5", range: [{ kind: "line", val: 5 }], melee: false },
  { key: "shielding_blast1", label: "Blast 1, Range 10", range: [{ kind: "blast", val: 1 }, { kind: "range", val: 10 }], melee: false },
  { key: "shielding_burst1", label: "Burst 1", range: [{ kind: "burst", val: 1 }], melee: true },
];

/** The prototype weapon's Stripped Reactor Shielding profile id for one option (`mw_prototype_2:shielding_cone3`). */
export function shieldingProfileId(weaponId: string, key: string): string {
  return `${weaponId}:${key}`;
}

/** `weapons` with `config` applied to the Engineer's prototype weapon; every other weapon unchanged. */
export function applyPrototypeConfig(weapons: WeaponProfile[], config: PrototypeConfig | undefined): WeaponProfile[] {
  if (config === undefined) return weapons;
  return weapons.map((w) => {
    if (!PROTOTYPE_WEAPON_IDS.has(w.id)) return w;
    const out: WeaponProfile = { ...w };
    if (config.weaponType !== undefined) {
      out.type = config.weaponType;
      out.range = config.weaponType === "Melee" ? [{ kind: "threat", val: 1 }] : [{ kind: "range", val: 10 }];
    }
    if (config.damageType !== undefined) out.damage = { ...w.damage, type: config.damageType };
    if (config.limitedUses !== undefined) out.limited = config.limitedUses;
    const revisions = REVISED_WEAPON_IDS.has(w.id) ? (config.revisions ?? []) : [];
    if (revisions.includes("optics")) out.accuracy = (out.accuracy ?? 0) + 1;
    if (revisions.includes("computer")) out.smart = true;
    if (revisions.includes("shielding")) {
      const { profiles: _own, ...base } = out;
      void _own;
      out.profiles = [
        ...(out.profiles ?? []),
        ...SHIELDING.filter((s) => config.weaponType === undefined || s.melee === (config.weaponType === "Melee")).map((s) => ({ ...base, id: shieldingProfileId(w.id, s.key), name: `${w.name} (Stripped Reactor Shielding: ${s.label})`, range: s.range, selfHeat: (w.selfHeat ?? 0) + 2 })),
      ];
    }
    return out;
  });
}
